import { useCallback } from 'react';
import { useModalStore } from '../store/modal.store';
import type { ModalId } from '../types/modal.types';

/**
 * Hook for managing a specific modal
 * @param id - Modal identifier
 * @returns Object with modal state and control functions
 *
 * @example
 * const { isOpen, open, close } = useModal('report');
 * <button onClick={() => open({ userId: 123 })}>Report User</button>
 */
export function useModal(id: ModalId) {
  const { openModal, closeModal, isModalOpen, getModalProps } = useModalStore();

  const open = useCallback(
    (props?: Record<string, unknown>) => {
      openModal(id, props);
    },
    [id, openModal]
  );

  const close = useCallback(() => {
    closeModal(id);
  }, [id, closeModal]);

  return {
    isOpen: isModalOpen(id),
    open,
    close,
    props: getModalProps(id),
  };
}

/**
 * Hook for managing all modals
 * @returns Object with global modal control functions
 *
 * @example
 * const { openModal, closeAllModals } = useModals();
 * openModal('settings', { section: 'privacy' });
 */
export function useModals() {
  const { openModal, closeModal, closeAllModals } = useModalStore();

  return {
    openModal,
    closeModal,
    closeAllModals,
  };
}
